import axios from 'axios';
import { useAuth } from 'contexts/auth';
import { useWebsocket } from 'contexts/websocket';
import { omit } from 'lodash';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import accountService from 'services/account';
import assignmentService from 'services/assignment';
import courseService from 'services/course';
import rubricService from 'services/rubric';
import { resolveFeatureFlags } from 'utils/helper';

const AppContext = createContext();
const useApp = () => useContext(AppContext);

const mergeAccountsAndUsers = (accounts, users) => {
    return accounts
        .filter(({ active }) => active)
        .map((account) => {
            const user = users.find(({ id }) => id === account.user_id);
            const userAccounts = accounts.filter(({ user_id }) => user_id === user.id);
            const isPending = userAccounts.some(({ login_activity }) => Boolean(login_activity));
            return {
                ...omit(account, ['_id', 'id']),
                ...omit(user, ['_id', 'id']),
                owner_account_id: account.id,
                user_id: user.id,
                is_pending: isPending,
            };
        });
};

const AppProvider = (props) => {
    const { auth, setAuth } = useAuth();
    const location = useLocation();
    const { setPingPayload, onMessage } = useWebsocket();

    // default value null means uninitialized unless stated otherwise
    const [course, setCourse] = useState(null);
    const [accountUsers, setAccountUsers] = useState(null); // account document and user document merged
    const [assignments, setAssignments] = useState(null);
    const [currentAssignmentId, setCurrentAssignmentId] = useState(null); // the assignment you are currently looking at
    const [batch, setBatch] = useState(null); // ongoing backend jobs for current assignment
    const [feedback, setFeedback] = useState(null); // feedback for current assignment and current accountUser
    const [submissions, setSubmissions] = useState(null); // submissions for current assignment
    const [previewSubmissions, setPreviewSubmissions] = useState(null); // preview submissions for current assignment, used for fine-tuning
    const [rubrics, setRubrics] = useState(null);
    const [department, setDepartment] = useState(null);

    const isLoading = !course || !accountUsers || !assignments;
    const selfAccountUser = useMemo(
        () => accountUsers?.find(({ owner_account_id }) => owner_account_id === auth.id),
        [accountUsers, auth?.id]
    );
    const instructorAccountUsers = useMemo(
        () => accountUsers?.filter(({ role }) => role === 'instructor'),
        [accountUsers]
    );
    const staffAccountUsers = useMemo(
        () => accountUsers?.filter(({ role }) => role === 'instructor' || role === 'ta'),
        [accountUsers]
    );
    const studentAccountUsers = accountUsers?.filter(({ active, role }) => active && role === 'student');
    const currentAssignment = assignments?.find(({ id }) => id === currentAssignmentId);

    const isGradingFeatureDisabled = useMemo(() => department?.features?.assignment_grading === false, [department]);
    const isFeedbackFeatureDisabled = useMemo(() => department?.features?.assignment_feedback === false, [department]);
    const isAcceptAllFeatureDisabled = useMemo(
        () => department?.features?.assignment_accept_all === false,
        [department]
    );
    const isInlineCommentsFeatureDisabled = useMemo(
        () => department?.features?.assignment_inline_comments === false,
        [department]
    );

    const updateCourse = async (courseData) => {
        try {
            await courseService.updateCourse(auth.course_id, courseData);
            return setCourse((prev) => ({ ...prev, ...courseData }));
        } catch (e) {
            console.error(e);
        }
    };

    const addAccountUser = (accountUser) => {
        setAccountUsers((current) => [...current, accountUser]);
    };

    const removeAccountUser = (accountUser) => {
        setAccountUsers((current) =>
            current.filter(({ owner_account_id }) => owner_account_id !== accountUser.owner_account_id)
        );
    };

    const changeAccountUserRole = async (targetAccountUser, newRole) => {
        const newAccount = await accountService.updateAccount(targetAccountUser.owner_account_id, {
            role: newRole,
        });
        setAccountUsers((current) => {
            return current.map((accountUser) => {
                if (accountUser.owner_account_id !== targetAccountUser.owner_account_id) return accountUser;
                return {
                    ...accountUser,
                    ...omit(newAccount, ['_id', 'id']),
                    owner_account_id: newAccount.id,
                };
            });
        });
        if (targetAccountUser.owner_account_id === auth.id) {
            setAuth((current) => ({ ...current, ...newAccount }));
        }
    };

    useEffect(() => {
        if (!auth?.course_id) return;
        (async () => {
            const course = await courseService.findCourse(auth.course_id);
            setCourse(course);
        })();
    }, [auth?.course_id]);

    useEffect(() => {
        if (!course) return;
        (async () => {
            const [accounts, users, assignments, department] = await Promise.all([
                await courseService.listCourseAccounts(course.id),
                await courseService.listCourseUsers(course.id),
                await courseService.listCourseAssignments(course.id),
                await courseService.findCourseDepartment(course.id),
            ]);
            setAccountUsers(mergeAccountsAndUsers(accounts, users));
            setAssignments(assignments.map((assignment) => resolveFeatureFlags(assignment, department?.features)));
            setDepartment(department);
        })();
    }, [course]);

    useEffect(() => {
        if (location.pathname.startsWith('/assignments')) {
            const assignmentId = location.pathname.split('/')[2];
            if (!assignmentId) {
                setCurrentAssignmentId(null);
            }
            setCurrentAssignmentId(assignmentId);
        } else {
            setCurrentAssignmentId(null);
        }
    }, [location.pathname]);

    useEffect(() => {
        setBatch(null);
        setFeedback(null);
        setSubmissions(null);
        setPreviewSubmissions(null);

        if (!auth || !currentAssignmentId || !instructorAccountUsers) return;

        const controller = new AbortController();
        const signal = controller.signal;

        const fetchData = async () => {
            try {
                const instructorOwnerAccountIds = instructorAccountUsers.map(
                    ({ owner_account_id }) => owner_account_id
                );
                const [batch, feedback, submissions, previewSubmissions] = await Promise.all([
                    assignmentService.listAssignmentBatch(
                        currentAssignmentId,
                        JSON.stringify({
                            is_finished: false,
                            ...(auth.role === 'student'
                                ? { owner_account_id: auth.id }
                                : { owner_account_id: instructorOwnerAccountIds }),
                        }),
                        null,
                        null,
                        null,
                        { signal }
                    ),
                    assignmentService.listAssignmentFeedback(
                        currentAssignmentId,
                        auth.role === 'student' ? JSON.stringify({ owner_account_id: auth.id }) : undefined,
                        null,
                        null,
                        null,
                        { signal }
                    ),
                    assignmentService.listAssignmentSubmissions(
                        currentAssignmentId,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        { signal }
                    ),
                    assignmentService.listAssignmentSubmissions(
                        currentAssignmentId,
                        JSON.stringify({ type: 'fine-tune' }),
                        null,
                        null,
                        null,
                        { signal }
                    ),
                ]);
                setBatch(
                    batch.map((b) => {
                        if (!b.submissions) {
                            return {
                                ...b,
                                submissions: b.files.map((f) => ({
                                    submitted_at: b.created_on,
                                    files: [f],
                                })),
                            };
                        }
                        return b;
                    })
                );
                setFeedback(feedback);
                const taSectionIds = (selfAccountUser.sections || []).map((s) => `${s.id}`);
                setSubmissions(
                    submissions
                        .filter((submission) => {
                            if (auth.role === 'ta' && Boolean(currentAssignment.api_assignment_id)) {
                                const apiUser = submission.api?.user;
                                if (!apiUser) return false;
                                const studentSections = [];
                                if (apiUser.section) studentSections.push(apiUser.section);
                                if (Array.isArray(apiUser.sections)) studentSections.push(...apiUser.sections);
                                if (studentSections.length === 0) return true;
                                const studentSectionIds = studentSections.map((s) => `${s.id}`);
                                return studentSectionIds.some((sid) => taSectionIds.includes(sid));
                            }
                            return true;
                        })
                        .map((submission) => {
                            if (!submission.files) {
                                return { ...submission, files: [{ ...submission.data, api: submission.api }] };
                            }
                            return submission;
                        })
                );
                setPreviewSubmissions(
                    previewSubmissions.map((ps) => {
                        if (!ps.files) return { ...ps, files: [ps.data] };
                        return ps;
                    })
                );
            } catch (error) {
                if (axios.isCancel?.(error) || error.name === 'CanceledError') {
                    // request was cancelled
                } else {
                    console.error(error);
                }
            }
        };
        fetchData();

        return () => controller.abort();
    }, [auth, currentAssignment?.api_assignment_id, currentAssignmentId, instructorAccountUsers, selfAccountUser]);

    useEffect(() => {
        const cleanup = setPingPayload(() => {
            if (!currentAssignmentId || !batch) return {};
            return { batch_ids: batch.map(({ id }) => id) };
        });
        return cleanup;
    }, [currentAssignmentId, batch, setPingPayload]);

    useEffect(() => {
        const cleanup = onMessage(async (message) => {
            if (message.type !== 'pong' || !message.batches) return;

            const isAlreadyUpToDate = (batch, list) => {
                const currentCount = list?.filter(({ batch_id }) => batch_id === batch.id).length;
                return currentCount >= batch.submission_count;
            };

            const handleFineTuneBatch = async () => {
                const previewSubmissions = await assignmentService.listAssignmentSubmissions(
                    currentAssignmentId,
                    JSON.stringify({ type: 'fine-tune' })
                );
                setPreviewSubmissions(
                    previewSubmissions.map((ps) => {
                        if (!ps.files) return { ...ps, files: [ps.data] };
                        return ps;
                    })
                );
                setAssignments((current) => {
                    return current.map((assignment) => {
                        if (assignment.id !== currentAssignmentId) return assignment;
                        return {
                            ...assignment,
                            preview_files: assignment.preview_files.map((pf) => {
                                const matched = previewSubmissions.find(
                                    (ps) => ps.files[0].key === pf.data.key || ps.files[0].key.includes(pf.data.key)
                                );
                                return matched ? { ...pf, is_preview_up_to_date: true } : pf;
                            }),
                        };
                    });
                });
            };

            const handleRegularBatch = async () => {
                const submissions = await assignmentService.listAssignmentSubmissions(currentAssignmentId);
                setSubmissions(
                    submissions.map((s) => {
                        if (!s.files) return { ...s, files: [s.data] };
                        return s;
                    })
                );
            };

            await Promise.all(
                Object.values(message.batches).map(async (batch) => {
                    if (auth.role === 'student') {
                        const feedback = await assignmentService.listAssignmentFeedback(
                            currentAssignmentId,
                            JSON.stringify({ owner_account_id: auth.id })
                        );
                        setFeedback(feedback);
                    } else {
                        if (batch.type === 'fine-tune' && isAlreadyUpToDate(batch, previewSubmissions)) return;
                        if (!batch.type && isAlreadyUpToDate(batch, submissions)) return;

                        if (batch.type === 'fine-tune') {
                            await handleFineTuneBatch(batch);
                        } else {
                            await handleRegularBatch(batch);
                        }

                        if (batch.is_finished) {
                            setBatch((current) => current.filter((b) => b.id !== batch.id));
                        }
                    }
                })
            );
        });

        return cleanup;
    }, [auth?.id, auth?.role, currentAssignmentId, onMessage, previewSubmissions, submissions]);

    useEffect(() => {
        if (!auth?.user_id) return;
        (async () => {
            const rubrics = await rubricService.listRubrics(
                JSON.stringify({
                    user_id: auth.user_id,
                })
            );
            setRubrics(rubrics);
        })();
    }, [auth?.user_id]);

    return (
        <AppContext.Provider
            {...props}
            value={{
                isLoading,

                // core data
                course,
                accountUsers,
                setAccountUsers,
                instructorAccountUsers, // accountUsers with role 'instructor'
                staffAccountUsers, // accountUsers with role 'instructor' or 'ta'
                studentAccountUsers, // accountUsers with role 'student'
                assignments,
                setAssignments,
                department,

                // feature flags
                isGradingFeatureDisabled,
                isFeedbackFeatureDisabled,
                isAcceptAllFeatureDisabled,
                isInlineCommentsFeatureDisabled,

                // assignment related data
                currentAssignmentId,
                setCurrentAssignmentId,
                currentAssignment,
                batch,
                setBatch,
                feedback,
                setFeedback,
                submissions,
                setSubmissions,
                previewSubmissions,
                setPreviewSubmissions,
                rubrics,
                setRubrics,

                updateCourse,
                addAccountUser,
                removeAccountUser,
                changeAccountUserRole,
            }}
        />
    );
};

export { useApp };
export default AppProvider;
